import { useState } from 'react';

import { css } from '@emotion/react';
import { FormControl, FormHelperText, useTheme } from '@mui/material';
import { type Control, Controller, useFormContext } from 'react-hook-form';

import { type SuggestionsType } from '@modules/_shared/components/email-editor';
import {
  type RichEditorValue,
  createEmptyValue,
} from '@modules/_shared/components/email-editor/htmlValue';
import { RichEditor } from '@modules/_shared/components/email-editor/QuillRichEditor';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { RichEditorOutline } from '@modules/_shared/components/RichEditorOutline';
import { useLocale } from '@shared/hooks/locale';

type RaRichEditorSuggestionsProps = {
  control: Control<any>;
  required?: boolean;
  name: string;
  label: string;
  singleLine?: boolean;
  suggestions?: SuggestionsType;
  resetKey?: string;
  snippetLotId?: string;
  snippetDevelopmentId?: string;
  disabled?: boolean;
  onImageUpload?: (file: File) => Promise<string | null>;
};

const RaRichEditorField: React.FC<
  Pick<
    RaRichEditorSuggestionsProps,
    | 'singleLine'
    | 'suggestions'
    | 'required'
    | 'name'
    | 'snippetLotId'
    | 'snippetDevelopmentId'
    | 'onImageUpload'
    | 'disabled'
  > & {
    onChange: (event: RichEditorValue) => void;
  }
> = ({
  onChange,
  singleLine,
  suggestions,
  required,
  name,
  snippetLotId,
  snippetDevelopmentId,
  onImageUpload,
  disabled,
}) => {
  const { breakpoints } = useTheme();
  const { t, language } = useLocale();
  // Using getValues() here instead of passing down the value from the Controller will prevent rerendering from the parent when the value change. We just need the current value on reset, so when the `resetKey` changes.
  const { getValues } = useFormContext<{
    [key: string]: RichEditorValue | undefined;
  }>();

  const [value, setValue] = useState<RichEditorValue>(
    getValues(name) ?? createEmptyValue(),
  );

  return (
    <RichEditor
      value={value}
      required={required}
      snippetLotId={snippetLotId}
      snippetDevelopmentId={snippetDevelopmentId}
      onImageUpload={onImageUpload}
      disabled={disabled}
      onChange={v => {
        const hasContentChanged = v.getHtml() !== value.getHtml();

        setValue(v);

        if (hasContentChanged) {
          onChange(v);
        }
      }}
      singleLine={singleLine}
      css={css({
        ...(!singleLine
          ? {
              '.ql-editor': {
                minHeight: '300px',
                height: 'auto',
                maxHeight: 'calc(100vh - 400px)',
                overflow: 'auto',
                padding: '16px 8px',
                [breakpoints.up('sm')]: {
                  height: 'calc(100% - 47px)', // Minus the toolbar
                  maxHeight: 'calc(100vh - 520px)',
                },
              },
              '.ql-container': { height: '100%' },
            }
          : {}),
      })}
      suggestions={suggestions}
      snippetPreviewT={t}
      snippetPreviewLanguage={language}
    />
  );
};

export const RaRichEditorSuggestions: React.FC<
  RaRichEditorSuggestionsProps
> = ({
  name,
  singleLine = false,
  label,
  required = false,
  control,
  suggestions,
  resetKey,
  snippetLotId,
  snippetDevelopmentId,
  onImageUpload,
  disabled,
}) => {
  const { t } = useLocale();
  return (
    <Controller<{ [key: string]: RichEditorValue | undefined }>
      name={name}
      control={control}
      rules={{ required: required ? t('This field is required') : undefined }}
      render={({ field: { onChange }, fieldState: { error } }) => (
        <FormControl fullWidth error={error != null}>
          {label != null && <RaLabel label={label} required={required} />}
          <RichEditorOutline error={error != null} disabled={disabled}>
            <RaRichEditorField
              required={required}
              onChange={onChange}
              singleLine={singleLine}
              suggestions={suggestions}
              name={name}
              key={resetKey}
              snippetLotId={snippetLotId}
              snippetDevelopmentId={snippetDevelopmentId}
              onImageUpload={onImageUpload}
              disabled={disabled}
            />
          </RichEditorOutline>
          {error != null && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};
